import * as React from 'react';
import { cn } from '@lightning/utils';

export interface SkillMarketplaceCardProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'onClick'> {
  title: string;
  description: string;
  creator: string;
  /** Optional decorative avatar beside the creator credit. */
  creatorAvatar?: React.ReactNode;
  /** Separate creator destination; never activates the skill card. */
  creatorHref?: string;
  /** Omit redundant attribution on a creator's own profile. */
  hideCreator?: boolean;
  category: string;
  compatibility: string[];
  featured?: boolean;
  /** Quiet discovery treatment; full metadata remains on the detail page. */
  minimal?: boolean;
  /** Optional category eyebrow for quiet discovery cards. */
  showCategory?: boolean;
  onOpen?: () => void;
}

/**
 * Shared discovery card for community-published agent skills.
 * Product surfaces own routing and brand tokens; this primitive owns the
 * accessible card interaction and repeated skill metadata.
 */
export const SkillMarketplaceCard = React.forwardRef<
  HTMLElement,
  SkillMarketplaceCardProps
>(
  (
    {
      title,
      description,
      creator,
      creatorAvatar,
      creatorHref,
      hideCreator = false,
      category,
      compatibility,
      featured = false,
      minimal = false,
      showCategory = false,
      onOpen,
      className,
      ...props
    },
    ref,
  ) => {
    const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.target !== event.currentTarget) return;
      if (!onOpen || (event.key !== 'Enter' && event.key !== ' ')) return;
      event.preventDefault();
      onOpen();
    };

    return (
      <article
        ref={ref}
        tabIndex={onOpen && !creatorHref ? 0 : undefined}
        role={onOpen && !creatorHref ? 'link' : undefined}
        onClick={onOpen}
        onKeyDown={handleKeyDown}
        className={cn(
          'group grid min-h-[118px] grid-cols-1 gap-4 overflow-hidden rounded-[12px] border border-[#DDE7EF] bg-[#F6FAFF] px-5 py-4 text-left transition duration-300 md:grid-cols-[minmax(0,1fr)_190px] md:items-center md:gap-6 md:px-6',
          onOpen &&
            'cursor-pointer hover:border-[#BFCFDB] hover:bg-[#EEF4FA] hover:shadow-[0_8px_24px_rgba(23,50,87,0.06)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4B7C] focus-visible:ring-offset-2',
          minimal && 'border-[#D5DFEA] bg-white py-5 md:grid-cols-1 hover:bg-[#F6FAFF] hover:shadow-none',
          className,
        )}
        {...props}
      >
        <div className="min-w-0">
          {minimal && showCategory && (
            <span className="mb-3 block font-ef-circular text-[12px] font-normal text-[#877449]">
              {category}
            </span>
          )}
          <h2 className={cn(
            'font-feature-deck text-[20px] font-normal leading-tight text-[#173257] sm:text-[24px]',
            minimal && 'font-ef-circular text-[18px] font-normal leading-snug sm:text-[20px]',
          )}>
            {creatorHref && onOpen ? <button type="button" onClick={(event) => { event.stopPropagation(); onOpen(); }} className="text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4B7C]">{title}</button> : title}
          </h2>
          <p className="mt-1.5 line-clamp-2 font-ef-circular text-[14px] leading-5 text-[#5E6773] md:line-clamp-1">
            {description}
          </p>
          {minimal ? (!hideCreator && (
            <p className="mt-2.5 flex items-center gap-2 font-ef-circular text-[12px] font-light text-[#707C88]">
              {creatorHref ? <a href={creatorHref} onClick={(event) => event.stopPropagation()} className="inline-flex items-center gap-2 rounded hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4B7C]" aria-label={`View ${creator}'s profile`}>{creatorAvatar}<span>By {creator}</span></a> : <>{creatorAvatar}<span>By {creator}</span></>}
            </p>
          )) : <div className="mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-1 font-ef-circular text-[12px] uppercase tracking-[0.06em] text-[#5E6773]">
            <span className="font-medium text-[#877449]">{category}</span>
            {featured ? (
              <>
                <span aria-hidden="true" className="text-[#C7D1D9]">·</span>
                <span>Featured</span>
              </>
            ) : null}
            <span aria-hidden="true" className="text-[#C7D1D9]">·</span>
            <span className="normal-case tracking-normal">
              By <span className="font-medium text-[#526575]">{creator}</span>
            </span>
          </div>}
        </div>

        {!minimal && <div className="flex items-center gap-4 border-t border-[#E7EDF2] pt-3 md:h-full md:items-center md:justify-end md:border-l md:border-t-0 md:pl-6 md:pt-0">
          <div className="text-left md:text-right">
            <p className="font-ef-circular text-[11px] font-medium uppercase tracking-[0.08em] text-[#5E6773]">
              Works with
            </p>
            <p className="mt-0.5 font-ef-circular text-[12px] text-[#526575]">
              {compatibility.join(' · ')}
            </p>
          </div>
        </div>}
      </article>
    );
  },
);

SkillMarketplaceCard.displayName = 'SkillMarketplaceCard';
